import React, { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * ColumnResizeHandle — the grab strip on a column header's right edge.
 *
 * Product owner, 2026-08-09: *"maybe make the columns manual adjustmennt by the
 * user?"* Drag it to resize the column it sits on; **double-click it to put that
 * one column back to its measured default**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 HOW A DRAG IS TOLD APART FROM A SORT — A DEDICATED HIT AREA, NOT A
 * THRESHOLD. THIS IS THE ONE STRUCTURAL TRAP IN THE WHOLE FEATURE.
 * ═══════════════════════════════════════════════════════════════════════════
 * A sortable header is a real `<button>` that fills its `<th>` (`w-full`), and
 * both gestures start with a pointerdown on the same cell. Two ways to separate
 * them were available:
 *
 *   ❌ A MOVEMENT THRESHOLD on the header button — "> 4px means resize". That
 *      is the device `OrderTableView`'s item-preview uses to protect its
 *      scrollbar, and it is wrong here: it makes the WHOLE header a resize
 *      surface, so an imprecise click anywhere along a heading nudges the
 *      column instead of sorting, and there is no cursor affordance saying
 *      where the resize lives.
 *   ✅ A DEDICATED HIT AREA — this element. It is a SIBLING of the button
 *      inside the `<th>`, absolutely positioned over the cell's right edge and
 *      painted on top of it. **A pointer event on this span never reaches the
 *      button, because the button is not its ancestor**, so a drag structurally
 *      cannot fire a sort — there is no guard to remember and no threshold to
 *      tune. A click anywhere else on the heading still sorts, exactly as
 *      before.
 *
 * 🪤 **THE HANDLE MUST BE OUTSIDE THE BUTTON, NOT INSIDE IT.** Inside, every
 * pointerup would bubble to the button and sort the table on every drag. That
 * is why `ui/SortableHeader` takes a `children` slot rendered AFTER its button
 * rather than the handle being passed as part of the label.
 *
 * ⚠️ It also means the `<th>` must be a containing block — `SortableHeader`
 * adds `relative` when it is given children. Without it the handle positions
 * against the nearest positioned ancestor, which is the group card, and every
 * handle in the table stacks up on the card's right edge.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 THE DRAG WORKS IN RENDERED PIXELS AND STORES DECLARED ONES
 * ═══════════════════════════════════════════════════════════════════════════
 * The table is `table-fixed` + `w-full` with a declared `minWidth`. When the
 * declared total is NARROWER than the scrollport the table stretches to fill
 * it and every column is scaled up in proportion — so the rendered width of a
 * column is **not** its declared width, and a naive `declared + dx` drags the
 * column edge somewhere other than where the pointer is. With the columns
 * narrowed the factor can reach 1.8, at which point a 100px drag moves the edge
 * 180px and reads as broken.
 *
 * So: the target is computed in RENDERED space (`startRendered + dx`, which is
 * exactly where the pointer is) and converted back to a declared width that
 * will RENDER at that target. With `S` the scrollport width and `O` the sum of
 * the other columns' declared widths — both constant for the gesture:
 *
 *   no stretch (O + target ≥ S):  declared = target
 *   stretching (O + target < S):  declared = target·O / (S − target)
 *
 * The second is the solution of `declared · S / (O + declared) = target`, and
 * the two agree at the boundary, so the drag has no seam where the table stops
 * stretching. `S` is measured, never assumed; with no measurement yet the
 * no-stretch branch is used, which is the correct answer for every table that
 * already overflows its scrollport.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ♿ THE KEYBOARD PATH IS NOT OPTIONAL
 * ═══════════════════════════════════════════════════════════════════════════
 * A drag handle that only works with a mouse is not finished. This is the ARIA
 * **window splitter** pattern: `role="separator"` + `tabIndex={0}` +
 * `aria-valuenow`/`min`/`max`, so the control announces its current width and
 * its bounds rather than merely existing.
 *
 *   `ArrowLeft` / `ArrowRight`   ± 10px   (± 50px with Shift)
 *   `Home`                       back to this column's measured default
 *
 * ⚠️ **`role="separator"` DOES NOT PICK UP THE APP'S GLOBAL FOCUS RING.**
 * `client/src/index.css:12-18` scopes that to `button`, `a` and
 * `[role="button"]`, so this element must carry its own visible indicator or a
 * keyboard user has no idea where they are. It uses a `focus-visible` fill plus
 * a ring — `focus-visible` rather than `focus`, because a pointer user has
 * already been told where they are by the cursor and the hover fill.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🎨 THE RESTING STATE (2026-08-09) — AND WHY IT IS NOT THE GHOST WEIGHT
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ **THIS BLOCK USED TO SAY "The resting look is the **ghost** weight … no
 * fill and no border until hover or `focus-visible`". THAT SHIPPED AND THE
 * PRODUCT OWNER REJECTED IT**, testing the drag increment: *"I think we should
 * have a very light colour for the dragable handles for the table column
 * headers. currently we have to hover to try and find them"*. The ghost
 * argument — fifteen resting hairlines would compete with the expanded row —
 * was about a full-height RULE. It does not survive contact with a control
 * nobody can locate.
 *
 * 🔑 **THE VERDICT THAT SETS THE BAR: THIS HANDLE IS THE SOLE CARRIER, WHERE
 * THE SORT CARET NEXT DOOR IS REDUNDANT — SO THE SAME ANSWER DOES NOT APPLY.**
 * The caret's resting contrast is deliberately faint (1.80 / 2.18 on the order
 * table) and that was acceptable because the control's identity is carried by a
 * real `<button>`, its focus ring, a `title` and `aria-sort` — the caret only
 * adds direction. Enumerate the same list here and every entry is hover-only or
 * invisible: the `col-resize` cursor needs the pointer already on the 9px
 * strip; the `title` needs the same; the focus ring needs a tab stop to have
 * been reached; and `role="separator"` + `aria-valuenow` are announced to
 * assistive technology and to nobody else. **Nothing else on screen says the
 * control exists**, so WCAG 1.4.11's 3:1 non-text minimum applies to this mark
 * itself and a caret-level 1.80 would not be enough.
 *
 * 📏 **MEASURED against the background the cell REALLY has**, which is not
 * white-on-both-themes and not the order table's `#f9fafb`: the Breakdown's
 * sticky header cells carry `GAG_TABLE_HEADER_BG` = `bg-white dark:bg-gray-800`,
 * and `index.css`'s `html.dark .bg-white { … #1e293b !important }` beats the
 * non-important `dark:bg-gray-800`, so the two real backgrounds are
 * **`#ffffff` light and `#1e293b` dark**.
 *
 *                          rest        hover / focus-visible
 *     light (#ffffff)      4.02:1  →   5.17:1
 *     dark  (#1e293b)      3.91:1  →   5.75:1
 *
 * Both resting figures clear 3:1, and identically across all 75 handles on the
 * page (15 columns × 5 group tables). The two themes rest within 0.11 of each
 * other because the base shade differs per theme (`gray-600` / `gray-400`)
 * while the opacity does not.
 *
 * 🚨 **THE HOVER STEP IS `opacity`. A COLOUR ALSO CHANGES, BUT NOTHING RESTS ON
 * IT.** `index.css` carries a table of global `!important` dark-mode overrides
 * that has silently killed two meaning-carrying colours in this app already (a
 * chevron, a caret hover), and an `!important` author declaration beats any
 * non-important rule whatever its specificity.
 * ⚠️ **Grepped again for THIS property rather than inherited from the caret's
 * note, because the caret's fact is about `color` and this is
 * `background-color`, and the answer differs:** the dark background overrides
 * cover `.bg-white`, `.bg-gray-50`, `.bg-gray-50/50`, `.bg-gray-100`,
 * `.bg-gray-200`, the `-50`/`-100`/`-200` tints of blue/green/red/yellow/amber/
 * purple/orange/indigo, and named `hover:bg-*` variants — **and nothing at all
 * on `bg-gray-300`…`bg-gray-900` or on `bg-blue-400/500/600`.** So a colour
 * step on these shades is live in dark mode, unlike `dark:hover:text-*`.
 * It is still not what the step RESTS on: opacity appears in no override table
 * at any shade, so the step cannot be killed by a shade someone changes later.
 * Kill the colour and the opacity alone still steps **4.02 → 7.56** light and
 * **3.91 → 5.76** dark (measured by forcing the grip to `opacity: 1` with its
 * grey intact), so the affordance degrades to "quieter", never to "gone".
 *
 * 🪤 **The blue had to move, and the measurement is why.** The shipped hover was
 * `group-hover:bg-blue-400` in light = **2.54:1 on white** — FAINTER than the
 * new resting state's 4.02, so "hovering" would have made the handle *harder*
 * to see. It is `blue-600` in light now (5.17) and stays `blue-400` in dark
 * (5.75). `blue-500` was measured too (3.68 on white) and is also below the
 * resting state; there is no middle option, which is why the step is two shades.
 * 🪤 Each blue is written `dark:group-hover:` as well as `group-hover:`, giving
 * it specificity (0,3,0) — a bare `group-hover:` is (0,2,0) and would TIE with
 * the resting `dark:bg-gray-400`, resolved by stylesheet order rather than by
 * intent. The opacity pair has no such conflict, which is a third reason it is
 * the load-bearing half.
 *
 * 🚨 **A FAINT VERTICAL LINE AT A COLUMN BOUNDARY IS EXACTLY WHAT A BORDER
 * LOOKS LIKE — so the resting mark is NOT a line.** It is a short, rounded,
 * vertically-centred GRIP (2px × half the cell height) sitting on the column
 * boundary: the splitter idiom, and unlike a rule it cannot be read as chrome,
 * because nothing in this app draws a border as a stub at mid-height. The four
 * things that tell a user it is a control: it is a stub, not a rule; the header
 * row has no other vertical marks of any kind; pointing at it turns the cursor
 * to `col-resize` and the grip brightens and turns blue; and it is a tab stop
 * that announces "Resize Status column" with a value and bounds.
 * ⚠️ **Do not "tidy" it back to `h-full`.** The full-height version is the one
 * that reads as a border, and it is why the resting state could not simply be
 * the old hairline with a colour in it.
 *
 * 🔒 **THE MARK IS NOT THE HIT AREA AND MUST NEVER BECOME IT.** Everything
 * above describes the inner `aria-hidden` span. The outer strip's geometry —
 * `absolute right-0 top-0 h-full w-[9px] … justify-end` — is unchanged by this
 * work and is what `elementFromPoint` sees; see the overhang note on the
 * element itself.
 *
 * @param {string} columnKey   passed straight back to the callbacks.
 * @param {string} label       for the accessible name ("Resize Status column").
 * @param {number} width       the column's DECLARED width — `aria-valuenow`.
 * @param {number} min         `aria-valuemin`; the hook clamps, this announces.
 * @param {number} max         `aria-valuemax`.
 * @param {number} totalWidth  declared sum of ALL columns (for `O` above).
 * @param {number|null} scrollportWidth  measured; `null` before first layout.
 * @param {(key: string, px: number) => void} onResize  a DECLARED width.
 * @param {(key: string) => void} onReset
 */

/** ⌨️ Arrow steps. Shift is the coarse one, matching every slider in the app. */
export const COLUMN_NUDGE_PX = 10;
export const COLUMN_NUDGE_SHIFT_PX = 50;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THE RIGHT-EDGE ANCHOR — why widening a column must not lose the columns
 * to its right. (2026-08-09)
 * ═══════════════════════════════════════════════════════════════════════════
 * Product owner: *"I can push the actions column off the screen by adjusting
 * the column progrss/actions"*.
 *
 * 🔍 **Diagnosed before it was fixed, and three of the four obvious causes were
 * ruled OUT by measurement rather than by reading:**
 *   - The per-column ceiling IS applied to `actions` (one shared
 *     `MAX_GAG_COLUMN_WIDTH`, not a per-column list with a gap in it).
 *   - The group-card wrapper is already pinned to the LIVE `totalWidth`, not to
 *     the defaults' sum — that earlier fix is complete.
 *   - The scrollport really can reach the far edge: driving `scrollLeft` to its
 *     maximum put the Actions kebab fully in view at every width tried, right up
 *     to every column at its 600px ceiling.
 *
 * ✅ **What actually happens** (measured in Chromium at 2048×1080, where the
 * table fits exactly before the drag): widening Progress by 500 takes the table
 * from 1724px to 2190px inside a 1724px scrollport while `scrollLeft` stays at
 * **0**, so the Actions column lands 405px beyond the right edge. That is
 * ordinary spreadsheet behaviour — the left edge is anchored and the surplus
 * goes off the right — and it is only a defect here because **Actions is the
 * sole route to delete / restore / assign**, and the scrollbar that would bring
 * it back sits at the bottom of a `100vh`-tall inner region, below the fold.
 *
 * 🔑 **THE FIX: if the table's right-hand edge was IN VIEW when the drag began,
 * keep it in view as the drag grows the table.** Nothing else changes — a user
 * already scrolled away from the right end keeps today's behaviour exactly, so
 * the anchor cannot yank the view around under someone who was reading the
 * left-hand columns.
 *
 * 🔴 **Why not bound the TOTAL width instead** (the other option): the per-column
 * ceiling already bounds it, at 15 × 600 = 9,000px, and a second ceiling on the
 * sum would be a number with no measurement behind it — the exact shape
 * `MAX_GAG_COLUMN_WIDTH`'s own docblock warns against. Worse, it would make a
 * drag stop responding part-way through with nothing on screen saying why.
 * A bound also would not have fixed the report: 2190px is far under any
 * plausible cap, and Actions was still off screen.
 *
 * 🔴 **Why not a sticky-right Actions column**, which would be structurally
 * unreachable-proof and was weighed rather than dismissed: a sticky `<td>` does
 * not inherit its `<tr>`'s background (the same fact that forces the sticky
 * `<thead>` here to restate its fill ON the cell), so it would have to replicate
 * every row state this table paints — base, `ROW_HOVER`, the expanded frame and
 * the `opacity-40` dimming — and get each right in both themes. It would also
 * permanently cover 60px of the right-hand edge on a surface the owner is
 * actively comparing, which is a product change he did not ask for. The cost is
 * real and the benefit over this fix is limited to the case where the user has
 * deliberately scrolled away from the right end. **If he later asks for a pinned
 * Actions column, that is the design to reach for — this note is why it was not
 * taken now, not an argument that it is wrong.**
 */

/**
 * How much residual scroll range still counts as "the right edge is in view".
 *
 * 🪤 **NOT a guess, and NOT zero.** A region whose content fits exactly still
 * reports a few pixels of scroll range: measured at 2048×1080 the region read
 * `scrollWidth 1729` against `clientWidth 1724` with the table fully visible —
 * 5px contributed by the group cards' 1px borders inside the region and by
 * sub-pixel rounding. Testing `scrollLeft >= max` exactly would therefore have
 * decided "not at the right end" in precisely the case the owner reported, and
 * the anchor would never have fired. 8 clears the measured 5 with room for a
 * second border and for the fractional device pixels that appear at display
 * scalings other than 100% — the same rounding this file's sibling notes record
 * for the header seam.
 */
export const RIGHT_EDGE_SLOP_PX = 8;

/**
 * Was this scrollport showing its right-hand edge?
 *
 * Pure, so jsdom can pin the arithmetic even though it can neither lay out nor
 * scroll anything. `Math.max(0, …)` because a container that does not overflow
 * reports `scrollWidth < clientWidth` on some engines, which would otherwise
 * make the range negative and the comparison meaningless.
 */
export function isRightEdgeInView(scrollLeft, scrollWidth, clientWidth, slop = RIGHT_EDGE_SLOP_PX) {
  if (![scrollLeft, scrollWidth, clientWidth].every(Number.isFinite)) return false;
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  return maxScroll - scrollLeft <= slop;
}

/** The `scrollLeft` that brings the right-hand edge back into view. */
export function rightEdgeScrollLeft(scrollWidth, clientWidth) {
  if (![scrollWidth, clientWidth].every(Number.isFinite)) return 0;
  return Math.max(0, scrollWidth - clientWidth);
}

/**
 * The nearest ancestor that scrolls horizontally.
 *
 * 🔑 Resolved by COMPUTED STYLE rather than by a testid or a class, because this
 * component lives under `ui/` and must not learn what surface it is on. An
 * element scrolls on x when `overflow-x` computes to `auto` or `scroll`.
 */
export function findHorizontalScrollport(el) {
  for (let n = el && el.parentElement; n; n = n.parentElement) {
    const ox = getComputedStyle(n).overflowX;
    if (ox === 'auto' || ox === 'scroll') return n;
  }
  return null;
}

export default function ColumnResizeHandle({
  columnKey,
  label,
  width,
  min,
  max,
  totalWidth,
  scrollportWidth = null,
  onResize,
  onReset,
  // 🔑 The component lives under `ui/` and knows nothing about gags, so the
  // testid prefix is the caller's. The Breakdown passes `gag-col-resize`.
  testIdPrefix = 'col-resize',
}) {
  const elRef = useRef(null);
  // Captured on pointerdown and constant for the gesture. A ref, not state:
  // a pointermove must not wait for a re-render to know where it started.
  const dragRef = useRef(null);

  /**
   * Rendered target → declared width. See the block comment above.
   * Pure, so it is safe to run on every `pointermove`.
   */
  const toDeclared = useCallback((targetRendered, otherTotal) => {
    const S = scrollportWidth;
    if (!Number.isFinite(S) || otherTotal + targetRendered >= S) return targetRendered;
    // 🪤 `S - targetRendered` is guaranteed positive here: the branch condition
    // gives `targetRendered < S - otherTotal <= S`, and `otherTotal` is the sum
    // of at least fourteen positive widths. No divide-by-zero to guard.
    return (targetRendered * otherTotal) / (S - targetRendered);
  }, [scrollportWidth]);

  const onPointerDown = useCallback((e) => {
    // Only the primary button, and never a synthetic/secondary press.
    if (e.button !== 0) return;
    const th = elRef.current && elRef.current.closest('th');
    if (!th) return;
    // 🪤 The rendered width comes from the CELL, never from `width`: the two
    // differ by the stretch factor, which is the whole point of `toDeclared`.
    const startRendered = th.getBoundingClientRect().width;
    // 🔑 Decided ONCE, at pointerdown, and constant for the gesture — see the
    // right-edge anchor note above. Deciding it per `pointermove` would let the
    // anchor switch itself on halfway through a drag, because the first move
    // that grows the table is also the move that pushes the right edge out of
    // view: the answer would flip from false to true and the view would jump.
    const port = findHorizontalScrollport(th);
    dragRef.current = {
      startX: e.clientX,
      startRendered,
      otherTotal: totalWidth - width,
      port,
      anchorRight: !!port
        && isRightEdgeInView(port.scrollLeft, port.scrollWidth, port.clientWidth),
    };
    // 🪤 `setPointerCapture` is what stops a fast drag stranding the gesture
    // when the pointer outruns a 5px-wide strip. Without it the move events go
    // to whatever is under the cursor and the column freezes mid-drag.
    try { elRef.current.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    // Suppress the browser's text selection during the drag; the cell contains
    // a heading and a caret, both of which would otherwise highlight.
    e.preventDefault();
  }, [totalWidth, width]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const targetRendered = d.startRendered + (e.clientX - d.startX);
    onResize(columnKey, toDeclared(targetRendered, d.otherTotal));
  }, [columnKey, onResize, toDeclared]);

  const endDrag = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { elRef.current.releasePointerCapture(e.pointerId); } catch { /* jsdom */ }
  }, []);

  /**
   * Hold the right-hand edge in view while a drag grows the table.
   *
   * 🔑 **A LAYOUT effect keyed on `totalWidth`, and both halves matter.** The
   * new width reaches the DOM through the page's state, so the `pointermove`
   * that requested it cannot correct the scroll position itself — the columns
   * have not been re-laid-out yet, and `scrollWidth` still reports the old
   * total. Keying on `totalWidth` runs this exactly once per applied resize,
   * AFTER commit; `useLayoutEffect` rather than `useEffect` so the correction
   * lands in the same frame as the width and the table does not visibly jump
   * right and then snap back.
   *
   * ⚠️ **Gated on an ACTIVE drag** (`dragRef.current`), so a width arriving from
   * anywhere else — the keyboard, a double-click reset, `Fit columns to screen`,
   * another user's stored layout on first paint — never moves the user's scroll
   * position. `Fit columns to screen` in particular can shrink the table, and
   * scrolling on a shrink is exactly the yank this guard prevents.
   *
   * 🪤 Every one of `scrollLeft` / `scrollWidth` / `clientWidth` is read FRESH
   * here rather than reused from pointerdown: the whole point is that the total
   * has just changed, so a captured `scrollWidth` would be the stale one and the
   * anchor would land the view one resize step behind the pointer.
   */
  useLayoutEffect(() => {
    const d = dragRef.current;
    if (!d || !d.anchorRight || !d.port) return;
    d.port.scrollLeft = rightEdgeScrollLeft(d.port.scrollWidth, d.port.clientWidth);
  }, [totalWidth]);

  const onKeyDown = useCallback((e) => {
    const step = e.shiftKey ? COLUMN_NUDGE_SHIFT_PX : COLUMN_NUDGE_PX;
    if (e.key === 'ArrowLeft') {
      // ⌨️ `preventDefault` because the region this table lives in scrolls
      // horizontally, and an arrow key on a focused child scrolls it — which
      // would move the whole table sideways under the column being resized.
      e.preventDefault();
      onResize(columnKey, width - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onResize(columnKey, width + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onReset(columnKey);
    }
  }, [columnKey, width, onResize, onReset]);

  return (
    <span
      ref={elRef}
      role="separator"
      aria-orientation="vertical"
      // ♿ The accessible name. `label` is the heading text, so this reads
      // "Resize Status column". The expand/actions columns have no heading, so
      // the caller passes a name rather than an empty string.
      aria-label={`Resize ${label} column`}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-testid={`${testIdPrefix}-${columnKey}`}
      // A resize drag is never a table pan (`hooks/useDragScroll`).
      data-pan-skip=""
      title="Drag to resize · double-click to reset · arrow keys to adjust"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => onReset(columnKey)}
      onKeyDown={onKeyDown}
      // 🪤 **THE HIT AREA AND THE VISIBLE LINE ARE DIFFERENT SIZES, ON PURPOSE.**
      // The strip is 9px — a 1px hit area is unhittable with a real mouse — and
      // the LINE is the 1px inner span, pushed to the strip's right edge
      // (`justify-end`) so it sits ON the column boundary while the grab area
      // is the 9px immediately to its left. That is what a spreadsheet does.
      //
      // 🚨 **THE STRIP MUST STAY ENTIRELY INSIDE ITS OWN CELL. IT OVERHUNG BY
      // 4px (`-mr-1` + `justify-center`) UNTIL IT WAS MEASURED IN A BROWSER,
      // AND EVERY OVERHANGING PIXEL WAS UNREACHABLE.** Straddling the seam
      // looks strictly better and is dead: each `<th>` here is
      // `sticky z-[5]`, which makes it a STACKING CONTEXT, so this element's
      // `z-10` is resolved INSIDE its own cell and cannot lift it above the
      // NEXT cell — and the next `<th>` paints later simply by being later in
      // DOM order. Measured with `document.elementFromPoint` across the strip:
      // the inner 4px returned this handle, the outer 5px returned the
      // FOLLOWING column's sort button. A pointer aimed at the middle of the
      // strip — which is where a person aims, and where a test framework
      // clicks — hit the wrong element and did nothing at all.
      // ⚠️ Raising this `z-index` cannot fix it; only the `<th>`'s own tier
      // could, and that tier is fixed by the app's z-index contract.
      //
      // 🔑 `group` is UNNAMED and unambiguous here: `SortableHeader`'s own
      // `group` (which drives its caret opacity) is on the button, and the
      // button is this element's SIBLING rather than its ancestor.
      //
      // `touch-none` so a drag on a touch screen resizes instead of scrolling
      // the region out from under it.
      className="group absolute right-0 top-0 z-10 flex h-full w-[9px] cursor-col-resize
                 touch-none select-none items-stretch justify-end rounded-sm
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset
                 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
    >
      {/* ═══════════════════════════════════════════════════════════════════
          THE VISIBLE GRIP. **Visible AT REST** — see the resting-state block at
          the top of this file for the measured ratios and for why the ghost
          weight it replaced was rejected.

          `h-1/2 self-center` is the load-bearing half of "this is not a
          border": a stub at mid-height cannot be read as table chrome, where a
          full-height rule at a column boundary can be read as nothing else.
          `w-0.5` (2px) rather than the old 1px because a 1px line lands on a
          fractional device pixel at any display scaling other than 100% and
          anti-aliases away perhaps half its contrast — the same rounding this
          repo already records for the sticky-header seam.

          🚨 The OPACITY pair is the state dial and the COLOUR pair is
          decoration on top of it. Opacity appears in no `index.css`
          `!important` table at any shade, so the step survives whatever colour
          a later change picks; a colour-only step is one badly-chosen shade
          away from the inert `dark:hover:text-*` trap this app has already hit
          twice. **Do not simplify this to a colour step.**
          ═══════════════════════════════════════════════════════════════════ */}
      <span
        aria-hidden="true"
        className="h-1/2 w-0.5 self-center rounded-full transition
                   bg-gray-600 dark:bg-gray-400 opacity-75
                   group-hover:opacity-100 group-focus-visible:opacity-100
                   group-hover:bg-blue-600 dark:group-hover:bg-blue-400
                   group-focus-visible:bg-blue-600 dark:group-focus-visible:bg-blue-400"
      />
    </span>
  );
}
